import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import moment from "moment";
import { useMountEffect, useUpdateEffect } from "../../../../hooks/LifeCycle";
import TittleLine from "../../../../components/Common/TittleLine";
import { Section } from "../../../../components/CustomLayout/CustomLayoutStyles";
import CustomTable from "../../../../components/CustomTable/CustomTable";
import AdministracionUsuariosBusquedaForm from "../components/AdministracionUsuariosBusquedaForm";
import AdministracionUsuariosCrearEditarForm from "../components/AdministracionUsuariosCrearEditarForm";
import Colors from "../../../../themes/Colors";
import CustomAlert from "../../../../components/Common/CustomAlert";
import ModalLoader from "../../../../components/Common/ModalLoader";
import CheckRender from "../../../../components/Common/CheckRender";
import Tooltip from "../../../../components/Common/Tooltip";
import CustomIcon from "../../../../components/Common/CustomIcon";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  ConfirmationModal,
  FormModal
} from "../../../../components/CustomModal";
import UsuariosActions from "../../../../stores/usuarios/Actions";
import { DATE_FORMATS } from "../../../../utils/Constant";

const AdministracionUsuariosScreen = (props) => {
  const {
    usuarios = [],
    consultarUsuariosLoading = false,
    consultarUsuariosError = null,
    asignarRolLoading = false,
    asignarRolError = null,
    asignarRolData = null,
    removerRolLoading = false,
    removerRolError = null,
    removerRolData = null,
    consultarUsuarios,
    asignarRol,
    removerRol,
    authUserData
  } = props;

  const [formData, setFormData] = useState({
    searchTerm: ""
  });

  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({});

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);

  const [showFormModal, setShowFormModal] = useState(false);
  const [usuarioParaEditar, setUsuarioParaEditar] = useState(null);

  const buscarUsuarios = useCallback(() => {
    if (formData.searchTerm.trim()) {
      consultarUsuarios({
        searchTerm: formData.searchTerm.trim(),
        requestHeaders: {
          Authorization: `Bearer ${authUserData?.tokenStandVirtual}`
        }
      });
    }
  }, [consultarUsuarios, formData.searchTerm, authUserData?.tokenStandVirtual]);

  useMountEffect(() => {
    // No buscar automáticamente al montar
  });

  useEffect(() => {
    if (asignarRolData) {
      setAlertConfig({
        type: "success",
        title: "Rol asignado",
        message: `El rol ${asignarRolData.rol} ha sido asignado al usuario ${asignarRolData.usuario} exitosamente.`,
        onConfirm: () => {
          setShowAlert(false);
          buscarUsuarios();
        }
      });
      setShowAlert(true);
      handleCloseFormModal();
    }
  }, [asignarRolData]);

  useEffect(() => {
    if (removerRolData) {
      setAlertConfig({
        type: "success",
        title: "Rol removido",
        message: `El rol ${removerRolData.rol} ha sido removido del usuario ${removerRolData.usuario} exitosamente.`,
        onConfirm: () => {
          setShowAlert(false);
          buscarUsuarios();
        }
      });
      setShowAlert(true);
      setUsuarioSeleccionado(null);
    }
  }, [removerRolData]);

  useEffect(() => {
    if (consultarUsuariosError) {
      setAlertConfig({
        type: "error",
        title: "Error al consultar usuarios",
        message:
          consultarUsuariosError.mensaje || "Ha ocurrido un error inesperado.",
        onConfirm: () => {
          setShowAlert(false);
        }
      });
      setShowAlert(true);
    }
  }, [consultarUsuariosError]);

  useEffect(() => {
    if (asignarRolError) {
      setAlertConfig({
        type: "error",
        title: "Error al asignar rol",
        message: asignarRolError.mensaje || "Ha ocurrido un error inesperado.",
        onConfirm: () => {
          setShowAlert(false);
        }
      });
      setShowAlert(true);
    }
  }, [asignarRolError]);

  useEffect(() => {
    if (removerRolError) {
      setAlertConfig({
        type: "error",
        title: "Error al remover rol",
        message: removerRolError.mensaje || "Ha ocurrido un error inesperado.",
        onConfirm: () => {
          setShowAlert(false);
        }
      });
      setShowAlert(true);
    }
  }, [removerRolError]);

  const onStateChange = (key) => (value) => {
    setFormData((prevState) => ({
      ...prevState,
      [key]: value
    }));
  };

  const asignarRolUsuario = (record) => {
    setUsuarioParaEditar(record);
    setShowFormModal(true);
  };

  const handleCloseFormModal = () => {
    setShowFormModal(false);
    setUsuarioParaEditar(null);
  };

  const handleSubmitForm = (roleData) => {
    asignarRol({
      ...roleData,
      searchTerm: formData.searchTerm
    });
  };

  const confirmarRemoverRol = (record) => {
    if (
      !record.perfil ||
      !record.perfil.roles ||
      record.perfil.roles.length === 0
    ) {
      return;
    }
    setUsuarioSeleccionado({
      ...record,
      currentRole: record.perfil.roles[0]
    });
    setShowDeleteModal(true);
  };

  const handleRemoverRol = () => {
    setShowDeleteModal(false);
    removerRol({
      requestData: {
        username: usuarioSeleccionado.username,
        roleId: usuarioSeleccionado.currentRole
      },
      requestHeaders: {
        Authorization: `Bearer ${authUserData?.tokenStandVirtual}`
      },
      searchTerm: formData.searchTerm
    });
  };

  const processing =
    consultarUsuariosLoading || asignarRolLoading || removerRolLoading;

  const canManageUsers =
    authUserData?.prestador?.permisos?.includes("admin:manage_users");

  const columns = [
    {
      title: "Nombre",
      key: "name",
      render: (_, record) =>
        `${record.name || ""} ${record.lastName || ""}`.trim() || "N/A"
    },
    {
      title: "Usuario",
      key: "username",
      dataIndex: "username"
    },
    {
      title: "Email",
      key: "email",
      dataIndex: "email"
    },
    {
      title: "Departamento",
      key: "department",
      dataIndex: "department"
    },
    {
      title: "Rol Actual",
      key: "currentRole",
      render: (_, record) => {
        if (
          record.perfil &&
          record.perfil.roles &&
          record.perfil.roles.length > 0
        ) {
          return record.perfil.roles[0];
        }
        return "Sin rol asignado";
      }
    },
    {
      title: "Creado en",
      key: "createdOn",
      render: (_, record) => {
        if (record.perfil && record.perfil.createdOn) {
          return moment(record.perfil.createdOn).format(
            `${DATE_FORMATS.slash} HH:mm`
          );
        }
        return "Sin fecha de creación";
      }
    },
    {
      title: "Actualizado en",
      key: "modifiedOn",
      render: (_, record) => {
        if (record.perfil && record.perfil.modifiedOn) {
          return moment(record.perfil.modifiedOn).format(
            `${DATE_FORMATS.slash} HH:mm`
          );
        }
        return "Sin fecha de actualización";
      }
    },
    {
      title: "Actualizado por",
      key: "modifiedBy",
      render: (_, record) => {
        if (record.perfil && record.perfil && record.perfil.modifiedBy) {
          return record.perfil.modifiedBy;
        }
        return "Sin usuario de actualización";
      }
    },
    {
      title: "Acciones",
      key: "acciones",
      width: "120px",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
          {canManageUsers && (
            <Tooltip text="Asignar Rol" position="top">
              <CustomIcon
                icon={EditOutlined}
                style={{
                  fontSize: "25px",
                  color: Colors.primaryPrimera,
                  cursor: "pointer",
                  padding: "6px",
                  borderRadius: "4px",
                  transition: "all 0.3s ease"
                }}
                hoverColor={Colors.darkGreenPrimera}
                onClick={() => asignarRolUsuario(record)}
              />
            </Tooltip>
          )}

          {canManageUsers &&
            record.perfil &&
            record.perfil.roles &&
            record.perfil.roles.length > 0 && (
              <Tooltip text="Remover Rol" position="top">
                <CustomIcon
                  icon={DeleteOutlined}
                  style={{
                    fontSize: "25px",
                    color: Colors.error,
                    cursor: "pointer",
                    padding: "6px",
                    borderRadius: "4px",
                    transition: "all 0.3s ease"
                  }}
                  hoverColor={Colors.darkRed}
                  onClick={() => confirmarRemoverRol(record)}
                />
              </Tooltip>
            )}
        </div>
      )
    }
  ];

  useUpdateEffect(() => {
    setShowAlert(false);
    setAlertConfig({});
  }, []);

  return (
    <>
      <Section>
        <TittleLine title={"Administración de usuarios"} />
      </Section>

      <Section width={"100%"}>
        <AdministracionUsuariosBusquedaForm
          {...formData}
          onStateChange={onStateChange}
          onSearch={buscarUsuarios}
          isLoading={consultarUsuariosLoading}
        />
      </Section>

      <Section
        style={{
          padding: "0px",
          flexDirection: "column",
          gap: "10px",
          alignItems: "center"
        }}
      >
        <CheckRender allowed={usuarios?.length > 0}>
          <CustomTable
            columns={columns}
            dataSource={usuarios || []}
            showPagination={false}
            width={"100%"}
            rowKey="username"
            pageSize={10}
          />
        </CheckRender>
      </Section>

      <FormModal
        isOpen={showFormModal}
        onClose={handleCloseFormModal}
        title="Modificar Usuario"
      >
        <AdministracionUsuariosCrearEditarForm
          userData={usuarioParaEditar}
          onSubmit={handleSubmitForm}
          onCancel={handleCloseFormModal}
          isLoading={asignarRolLoading}
        />
      </FormModal>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleRemoverRol}
        title="¿Estás seguro que deseas remover el rol"
        highlightedText={usuarioSeleccionado?.currentRole}
        message={`Esta acción removerá el rol ${usuarioSeleccionado?.currentRole} del usuario ${usuarioSeleccionado?.username}. Esta acción no se puede deshacer.`}
        confirmText="Remover Rol"
        cancelText="Cancelar"
        confirmButtonColor={Colors.error}
        cancelButtonColor={Colors.gray}
        loading={removerRolLoading}
      />

      <CheckRender allowed={processing}>
        <ModalLoader />
      </CheckRender>

      <CheckRender allowed={showAlert}>
        <CustomAlert {...alertConfig} />
      </CheckRender>
    </>
  );
};

AdministracionUsuariosScreen.propTypes = {
  usuarios: PropTypes.array,
  consultarUsuariosLoading: PropTypes.bool,
  consultarUsuariosError: PropTypes.object,
  asignarRolLoading: PropTypes.bool,
  asignarRolError: PropTypes.object,
  asignarRolData: PropTypes.object,
  removerRolLoading: PropTypes.bool,
  removerRolError: PropTypes.object,
  removerRolData: PropTypes.object,
  consultarUsuarios: PropTypes.func.isRequired,
  asignarRol: PropTypes.func.isRequired,
  removerRol: PropTypes.func.isRequired,
  authUserData: PropTypes.object
};

const mapStateToProps = (state) => ({
  usuarios: state.usuarios.consultarUsuariosData?.users || [],
  consultarUsuariosLoading: state.usuarios.consultarUsuariosLoading,
  consultarUsuariosError: state.usuarios.consultarUsuariosError,
  asignarRolLoading: state.usuarios.asignarRolLoading,
  asignarRolError: state.usuarios.asignarRolError,
  asignarRolData: state.usuarios.asignarRolData,
  removerRolLoading: state.usuarios.removerRolLoading,
  removerRolError: state.usuarios.removerRolError,
  removerRolData: state.usuarios.removerRolData,
  authUserData: state.auth.authUserData
});

const mapDispatchToProps = {
  consultarUsuarios: UsuariosActions.consultarUsuarios,
  asignarRol: UsuariosActions.asignarRol,
  removerRol: UsuariosActions.removerRol
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(AdministracionUsuariosScreen);
